/**
 * SAAS-2 · 系统设置「订阅版」页签（仅订阅版出现）：帮助教程地址 + 更新说明增删改。
 * · 帮助教程地址 = tenant_settings.general.help_center_url（GET / PATCH /api/v1/settings/general 整包读写，只改这一项；后端校验 http(s) 并写操作日志）
 * · 更新说明 = GET /api/v1/release-notes/manage（含草稿）+ POST / PATCH / DELETE /api/v1/release-notes[/{id}]（后端写操作日志）
 * 看：system.settings.view；改：system.settings.update（老板 / 技术管理员），其它人只读。
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { useCan } from "@/auth/permissions";
import { useEdition } from "@/theme/useTheme";
import { settingsApi } from "@/services/settings";
import { SUB_QK, subscriptionApi, type ReleaseNote, type ReleaseNoteWrite } from "@/services/subscription";
import { ErrorState, LoadingState, PageShell } from "@/shared/ui/PageHeader";
import { Dialog } from "@/chrome/SubscriptionTools";
import { SettingsTabs } from "./SettingsTabs";

export function SubscriptionSettingsPage() {
  const edition = useEdition();
  return (
    <PageShell title="系统设置 · 订阅版" subtitle="帮助教程地址 + 更新说明 · 仅老板 + 技术管理员可改">
      <SettingsTabs />
      {edition === "subscription" ? (
        <>
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
            <Lock size={14} /> 权限：老板 + 技术管理员可编辑 · 其它角色只读 · 改动写操作日志
          </div>
          <HelpUrlCard />
          <NotesCard />
        </>
      ) : (
        <p className="text-[13px] text-slate-400">此功能仅订阅版提供</p>
      )}
    </PageShell>
  );
}

function HelpUrlCard() {
  const qc = useQueryClient();
  const canEdit = useCan("system.settings.update");
  const gen = useQuery({ queryKey: ["settings", "general"], queryFn: settingsApi.getGeneral });
  const [url, setUrl] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { if (gen.data && url === null) setUrl(gen.data.help_center_url ?? ""); }, [gen.data, url]);
  const save = useMutation({
    mutationFn: () => settingsApi.patchGeneral({ ...gen.data!, help_center_url: (url ?? "").trim() || null }),
    onSuccess: (d) => {
      qc.setQueryData(["settings", "general"], d);
      qc.invalidateQueries({ queryKey: SUB_QK.toolbar });
      setUrl(d.help_center_url ?? "");
      setMsg({ ok: true, text: d.help_center_url ? "已保存，顶栏「帮助教程」已出现" : "已清空，帮助教程入口不再显示" });
    },
    onError: (e) => setMsg({ ok: false, text: e instanceof Error ? e.message : "保存失败，请重试" }),
  });
  if (gen.isError) return <ErrorState message="通用设置加载失败" onRetry={() => gen.refetch()} />;
  if (!gen.data || url === null) return <LoadingState />;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid="help-url-card">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold">帮助教程</h3>
          <p className="mt-0.5 text-[12px] text-slate-500">填了：顶栏「帮助教程」、首页右下角、应用中心都出现这个链接（新标签页打开）；空着：这些入口都不显示</p>
        </div>
        {canEdit && (
          <button type="button" disabled={save.isPending} onClick={() => { setMsg(null); save.mutate(); }} className="h-9 shrink-0 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white disabled:opacity-50">
            {save.isPending ? "保存中…" : "保存"}
          </button>
        )}
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-medium text-slate-700">帮助教程地址</span>
        <input value={url} onChange={(e) => setUrl(e.target.value)} disabled={!canEdit} placeholder="例：https://help.公司域名.com/manual" aria-label="帮助教程地址"
          className="h-9 rounded-lg border border-slate-200 px-3 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50" />
      </label>
      {msg && <p className={`mt-2 text-[12px] ${msg.ok ? "text-emerald-600" : "text-rose-600"}`}>{msg.text}</p>}
    </section>
  );
}

const EMPTY: ReleaseNoteWrite = { title: "", body: "", is_published: false };

function NotesCard() {
  const qc = useQueryClient();
  const canEdit = useCan("system.settings.update");
  const q = useQuery({ queryKey: SUB_QK.manage, queryFn: subscriptionApi.manageNotes });
  const [editing, setEditing] = useState<{ id: string | null; v: ReleaseNoteWrite } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["subscription", "release-notes"] });
  const save = useMutation({
    mutationFn: (e: { id: string | null; v: ReleaseNoteWrite }) => (e.id ? subscriptionApi.updateNote(e.id, e.v) : subscriptionApi.createNote(e.v)),
    onSuccess: () => { setEditing(null); setErr(null); refresh(); },
    onError: (e) => setErr(e instanceof Error ? e.message : "保存失败，请重试"),
  });
  const toggle = useMutation({ mutationFn: (n: ReleaseNote) => subscriptionApi.updateNote(n.id, { is_published: !n.is_published }), onSettled: refresh });
  const del = useMutation({ mutationFn: (id: string) => subscriptionApi.deleteNote(id), onSettled: refresh });

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid="notes-card">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold">更新说明</h3>
          <p className="mt-0.5 text-[12px] text-slate-500">每次上线写几句；发布后顶栏「公告 / 更新说明」亮红点，每个人看过就消失。草稿只有这里看得到</p>
        </div>
        {canEdit && (
          <button type="button" onClick={() => { setErr(null); setEditing({ id: null, v: { ...EMPTY } }); }} className="inline-flex h-9 shrink-0 items-center gap-1 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white" data-testid="note-new">
            <Plus size={14} />写一条
          </button>
        )}
      </div>
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError || !q.data ? (
        <ErrorState message="更新说明加载失败" onRetry={() => q.refetch()} />
      ) : q.data.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-slate-400">还没有更新说明</p>
      ) : (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[12px] text-slate-500">
              <th className="py-2 font-medium">标题</th><th className="w-24 py-2 font-medium">状态</th><th className="w-32 py-2 font-medium">发布时间</th><th className="w-40 py-2" />
            </tr>
          </thead>
          <tbody>
            {q.data.map((n) => (
              <tr key={n.id} className="border-b border-slate-100 last:border-0" data-testid="note-row">
                <td className="py-2 pr-3"><div className="font-medium text-slate-900">{n.title}</div>{n.body && <div className="truncate text-[12px] text-slate-400">{n.body}</div>}</td>
                <td className="py-2">
                  {n.is_published ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[12px] text-emerald-700">已发布</span> : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[12px] text-slate-600">草稿</span>}
                </td>
                <td className="py-2 text-slate-500">{n.published_at ? new Date(n.published_at).toLocaleDateString("zh-CN") : "—"}</td>
                <td className="py-2 text-right">
                  {canEdit && (
                    <span className="inline-flex items-center gap-1">
                      <button type="button" onClick={() => toggle.mutate(n)} className="rounded px-2 py-1 text-[12px] text-blue-600 hover:bg-blue-50">{n.is_published ? "撤回" : "发布"}</button>
                      <button type="button" aria-label="编辑" onClick={() => { setErr(null); setEditing({ id: n.id, v: { title: n.title, body: n.body, is_published: n.is_published } }); }} className="rounded p-1 text-slate-500 hover:bg-slate-100"><Pencil size={14} /></button>
                      <button type="button" aria-label="删除" onClick={() => { if (window.confirm(`删除「${n.title}」？删了所有人都看不到`)) del.mutate(n.id); }} className="rounded p-1 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 size={14} /></button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <Dialog title={editing.id ? "编辑更新说明" : "写一条更新说明"} onClose={() => setEditing(null)}>
          <div className="flex flex-col gap-3" data-testid="note-editor">
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-slate-700">标题 *</span>
              <input value={editing.v.title} maxLength={200} onChange={(e) => setEditing({ ...editing, v: { ...editing.v, title: e.target.value } })} aria-label="标题"
                className="h-9 rounded-lg border border-slate-200 px-3 text-[13px] focus:border-blue-500 focus:outline-none" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-slate-700">内容</span>
              <textarea value={editing.v.body} maxLength={5000} rows={5} onChange={(e) => setEditing({ ...editing, v: { ...editing.v, body: e.target.value } })} aria-label="内容"
                className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] focus:border-blue-500 focus:outline-none" />
            </label>
            <label className="flex items-center gap-2 text-[13px] text-slate-700">
              <input type="checkbox" checked={editing.v.is_published} onChange={(e) => setEditing({ ...editing, v: { ...editing.v, is_published: e.target.checked } })} />
              保存后立即发布（不勾 = 存草稿）
            </label>
            {err && <p className="text-[12px] text-rose-600">{err}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className="h-9 rounded-lg border border-slate-200 px-4 text-[13px]">取消</button>
              <button type="button" disabled={!editing.v.title.trim() || save.isPending} onClick={() => save.mutate({ ...editing, v: { ...editing.v, title: editing.v.title.trim() } })}
                className="h-9 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white disabled:opacity-50" data-testid="note-save">
                {save.isPending ? "保存中…" : "保存"}
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </section>
  );
}
